import type { MeasurementPreferences, TrackContributionCornerApex } from '@kangaroo/shared';

import { parseAimLogDateTimeMs as parseAimLogDateTimeMsInZone } from './aimLogDateTime';
import { formatRecordedAtMs } from './formatSessionDate';
import { buildMyChronLapsFromGps } from './gpsLapDetection';
import { getMeasurementPreferences } from './measurements';
import { formatOverviewChannelValue, formatTelemetrySeriesValue } from './telemetryMeasurements';
import { sessionMapDebugDuration } from './sessionMapDebug';

export type MyChronGpsPoint = {
  timecodeMs: number;
  lat: number;
  lon: number;
  alt: number;
  speed: number;
  fix?: number;
  satellites?: number;
};

export type MyChronLapRecord = {
  segment: number;
  lapNumber: number;
  durationMs: number;
  endTimeMs: number;
};

export type MyChronSeriesSample = {
  timecodeMs: number;
  value: number;
};

export type MyChronChannelMeta = {
  index: number;
  shortName: string;
  longName: string;
  unit: string;
  decimals?: number;
};

export type MyChronSessionMetadata = {
  logDate?: string;
  logTime?: string;
  driver?: string;
  vehicle?: string;
  venue?: string;
  circuit?: {
    contributionId?: string;
    trackId?: string;
    name?: string;
    cornerApexSuggestions?: TrackContributionCornerApex[];
  };
};

export type MyChronSessionExport = {
  format: string;
  channels: MyChronChannelMeta[];
  laps: MyChronLapRecord[];
  gps: MyChronGpsPoint[];
  series: Record<string, MyChronSeriesSample[]>;
  metadata?: MyChronSessionMetadata;
  stats?: {
    gpsSamples?: number;
    singleSamples?: number;
    multiSamples?: number;
    groupSamples?: number;
    skippedBytes?: number;
    recordedAtMs?: number;
  };
};

export type TrackMapPoint = {
  x: number;
  y: number;
  lat: number;
  lon: number;
  speedKph: number;
  timecodeMs: number;
};

export type TrackMapChannelOverlayPoint = TrackMapPoint & {
  value: number;
  heatColor: string;
};

export type TrackMapChannelOverlay = {
  id: string;
  label: string;
  unit: string;
  color: string;
  min: number;
  max: number;
  points: TrackMapChannelOverlayPoint[];
};

export type LapAnalysis = {
  lap: MyChronLapRecord;
  lapIndex: number;
  startMs: number;
  endMs: number;
  durationSec: number;
  rank: number;
  totalLaps: number;
  gapToBestSec: number | null;
  idealLapSec: number | null;
  topSpeedKph: number | null;
  averageSpeedKph: number | null;
  maxRpm: number | null;
  maxTempC: number | null;
  /** Per-lap AccX median — raw logger AccX is gravity-biased and must be centered for driver G. */
  accXBaseline: number | null;
  mapPoints: TrackMapPoint[];
};

export type ChannelReadout = {
  label: string;
  shortName: string;
  value: string;
  color: string;
  enabled: boolean;
};

const OVERVIEW_CHANNELS: Array<{
  label: string;
  shortNames: string[];
  color: string;
}> = [
  { label: 'Speed', shortNames: ['WSpd'], color: '#60A5FA' },
  { label: 'RPM', shortNames: ['RPM'], color: '#EF4444' },
  { label: 'Steering', shortNames: ['StrA'], color: '#A855F7' },
  { label: 'Lateral G', shortNames: ['AccY'], color: '#22D3EE' },
  { label: 'Longitudinal G', shortNames: ['AccX'], color: '#FACC15' },
  { label: 'Logger Temp', shortNames: ['LogT'], color: '#6366F1' },
  { label: 'Head Temp', shortNames: ['TC 1', 'TC 2'], color: '#22C55E' },
  { label: 'Battery', shortNames: ['VBat', 'VBIn'], color: '#EF4444' },
];

export function isMyChronSessionExport(value: unknown): value is MyChronSessionExport {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const record = value as Record<string, unknown>;
  return (
    Array.isArray(record.gps) && Array.isArray(record.laps) && typeof record.series === 'object'
  );
}

export function rawLoggerLaps(laps: MyChronLapRecord[] | null | undefined) {
  if (!Array.isArray(laps)) {
    return [];
  }

  return laps
    .filter((lap) => (lap.segment ?? 0) === 0 && lap.durationMs > 0)
    .sort((left, right) => left.lapNumber - right.lapNumber);
}

export function resolveRawLoggerLaps(session: MyChronSessionExport): MyChronLapRecord[] {
  const fromLogger = rawLoggerLaps(session.laps);
  if (fromLogger.length > 0) {
    return fromLogger;
  }

  if (session.gps.length >= 12) {
    return buildMyChronLapsFromGps(session.gps);
  }

  return [];
}

export function racingLaps(laps: MyChronLapRecord[] | null | undefined) {
  return rawLoggerLaps(laps).filter((lap) => lap.durationMs >= 15_000 && lap.durationMs <= 120_000);
}

export type LapExclusionReason = 'out' | 'in' | 'invalid' | 'other';

export type LapExclusion = {
  lapNumber: number;
  reason: LapExclusionReason;
};

export function racingLapsForAnalysis(
  session: MyChronSessionExport,
  exclusions: LapExclusion[] = [],
) {
  const excluded = new Set(exclusions.map((entry) => entry.lapNumber));
  return racingLaps(session.laps).filter((lap) => !excluded.has(lap.lapNumber));
}

export function lapWindow(lap: MyChronLapRecord) {
  return {
    startMs: lap.endTimeMs - lap.durationMs,
    endMs: lap.endTimeMs,
  };
}

function channelUnit(channels: MyChronChannelMeta[], shortName: string) {
  return channels.find((channel) => channel.shortName === shortName)?.unit ?? null;
}

function seriesInWindow(series: MyChronSeriesSample[] | undefined, startMs: number, endMs: number) {
  if (!series?.length) {
    return [];
  }

  return series.filter((sample) => sample.timecodeMs >= startMs && sample.timecodeMs <= endMs);
}

function maxInWindow(series: MyChronSeriesSample[] | undefined, startMs: number, endMs: number) {
  const values = seriesInWindow(series, startMs, endMs).map((sample) => sample.value);
  return values.length === 0 ? null : Math.max(...values);
}

function latestInWindow(series: MyChronSeriesSample[] | undefined, startMs: number, endMs: number) {
  const samples = seriesInWindow(series, startMs, endMs);
  return samples.at(-1)?.value ?? null;
}

function medianInWindow(series: MyChronSeriesSample[] | undefined, startMs: number, endMs: number) {
  const values = seriesInWindow(series, startMs, endMs)
    .map((sample) => sample.value)
    .sort((left, right) => left - right);

  if (values.length === 0) {
    return null;
  }

  const mid = Math.floor(values.length / 2);
  return values.length % 2 === 1 ? values[mid]! : (values[mid - 1]! + values[mid]!) / 2;
}

/** Raw MyChron AccX rises under braking; center against the lap baseline for signed driver G. */
export function driverLongitudinalG(
  accX: number | null,
  accXBaseline: number | null,
): number | null {
  if (accX === null) {
    return null;
  }

  if (accXBaseline === null) {
    return accX;
  }

  return accX - accXBaseline;
}

/** MyChron AccY sign is opposite our lateral G convention (left/right). */
export function driverLateralG(accY: number | null): number | null {
  if (accY === null) {
    return null;
  }

  return -accY;
}

function gpsInWindow(gps: MyChronGpsPoint[], startMs: number, endMs: number) {
  return gps.filter((point) => point.timecodeMs >= startMs && point.timecodeMs <= endMs);
}

export function gpsPointsInWindow(gps: MyChronGpsPoint[], startMs: number, endMs: number) {
  return gpsInWindow(gps, startMs, endMs);
}

export function interpolateGpsAtTime(
  gps: MyChronGpsPoint[],
  timecodeMs: number,
): MyChronGpsPoint | null {
  if (gps.length === 0) {
    return null;
  }

  if (timecodeMs <= gps[0]!.timecodeMs) {
    return { ...gps[0]!, timecodeMs };
  }

  const last = gps.at(-1)!;
  if (timecodeMs >= last.timecodeMs) {
    return { ...last, timecodeMs };
  }

  for (let index = 1; index < gps.length; index += 1) {
    const next = gps[index]!;
    const previous = gps[index - 1]!;
    if (timecodeMs > next.timecodeMs) {
      continue;
    }

    const spanMs = next.timecodeMs - previous.timecodeMs;
    const ratio = spanMs > 0 ? (timecodeMs - previous.timecodeMs) / spanMs : 0;

    return {
      lat: previous.lat + ratio * (next.lat - previous.lat),
      lon: previous.lon + ratio * (next.lon - previous.lon),
      alt: previous.alt + ratio * (next.alt - previous.alt),
      speed: previous.speed + ratio * (next.speed - previous.speed),
      timecodeMs,
      fix: next.fix,
      satellites: next.satellites,
    };
  }

  return null;
}

export function sliceGpsAtTimeBounds(
  gps: MyChronGpsPoint[],
  startMs: number,
  endMs: number,
): MyChronGpsPoint[] {
  if (gps.length === 0 || endMs < startMs) {
    return [];
  }

  const start = interpolateGpsAtTime(gps, startMs);
  const end = interpolateGpsAtTime(gps, endMs);
  if (!start || !end) {
    return [];
  }

  const inner = gps.filter((point) => point.timecodeMs > startMs && point.timecodeMs < endMs);
  const points = [start, ...inner];
  const last = points.at(-1)!;

  if (end.timecodeMs > last.timecodeMs + 0.5) {
    points.push(end);
  }

  return points;
}

export function sessionGpsCenter(
  session: MyChronSessionExport,
): { lat: number; lon: number } | null {
  if (session.gps.length === 0) {
    return null;
  }

  const sample = downsampleTrackPoints(session.gps, 120);
  const totals = sample.reduce(
    (accumulator, point) => ({
      lat: accumulator.lat + point.lat,
      lon: accumulator.lon + point.lon,
    }),
    { lat: 0, lon: 0 },
  );

  return {
    lat: totals.lat / sample.length,
    lon: totals.lon / sample.length,
  };
}

export function sessionTimeSpan(session: MyChronSessionExport) {
  if (session.gps.length === 0) {
    return null;
  }

  const first = session.gps[0]!;
  const last = session.gps.at(-1)!;

  return {
    startMs: first.timecodeMs,
    endMs: last.timecodeMs,
    midMs: first.timecodeMs + (last.timecodeMs - first.timecodeMs) / 2,
  };
}

export function sessionRecordedAtMs(session: MyChronSessionExport, timeZone?: string | null) {
  const meta = session.metadata;
  if (meta?.logDate) {
    return parseAimLogDateTimeMs(meta.logDate, meta.logTime, timeZone);
  }

  if (session.stats?.recordedAtMs) {
    return session.stats.recordedAtMs;
  }

  return null;
}

/** AIM footer dates use MM/DD/YYYY and HH:MM[:SS] in logger-local time. */
export function parseAimLogDateTimeMs(
  logDate: string,
  logTime?: string | null,
  timeZone?: string | null,
) {
  return parseAimLogDateTimeMsInZone(logDate, logTime, timeZone);
}

export function formatSessionRecordedAt(recordedAtMs: number, timeZone?: string | null) {
  return formatRecordedAtMs(recordedAtMs, timeZone, 'datetime');
}

export function downsampleTrackPoints<T>(points: T[], maxPoints: number) {
  if (points.length <= maxPoints) {
    return points;
  }

  const stride = Math.ceil(points.length / maxPoints);
  return points.filter((_, index) => index % stride === 0 || index === points.length - 1);
}

/** Preserve spikes by keeping min/max/mid samples inside each downsample bucket. */
export function downsampleSeriesEnvelope(
  series: MyChronSeriesSample[],
  maxPoints: number,
): MyChronSeriesSample[] {
  if (series.length <= maxPoints) {
    return series;
  }

  const bucketCount = Math.max(1, Math.floor(maxPoints / 3));
  const bucketSize = series.length / bucketCount;
  const result: MyChronSeriesSample[] = [];

  for (let bucketIndex = 0; bucketIndex < bucketCount; bucketIndex += 1) {
    const start = Math.floor(bucketIndex * bucketSize);
    const end = Math.min(series.length, Math.floor((bucketIndex + 1) * bucketSize));
    const bucket = series.slice(start, end);
    if (bucket.length === 0) {
      continue;
    }

    let minSample = bucket[0]!;
    let maxSample = bucket[0]!;
    for (const sample of bucket) {
      if (sample.value < minSample.value) {
        minSample = sample;
      }
      if (sample.value > maxSample.value) {
        maxSample = sample;
      }
    }

    const lastSample = bucket.at(-1)!;
    const picks = [minSample, maxSample, lastSample].sort(
      (left, right) => left.timecodeMs - right.timecodeMs,
    );
    for (const sample of picks) {
      if (!result.some((entry) => entry.timecodeMs === sample.timecodeMs)) {
        result.push(sample);
      }
    }
  }

  return result.sort((left, right) => left.timecodeMs - right.timecodeMs);
}

function mergeTraceTimecodes(...seriesList: MyChronSeriesSample[][]) {
  const timecodes = new Set<number>();
  for (const series of seriesList) {
    for (const sample of series) {
      timecodes.add(sample.timecodeMs);
    }
  }

  return [...timecodes].sort((left, right) => left - right);
}

function capTraceTimecodes(timecodes: number[], maxPoints: number) {
  if (timecodes.length <= maxPoints) {
    return timecodes;
  }

  const stride = Math.ceil(timecodes.length / maxPoints);
  return timecodes.filter((_, index) => index % stride === 0 || index === timecodes.length - 1);
}

const TRACK_VIEWBOX = { width: 640, height: 280, padding: 36 } as const;
const MAP_TRACK_MAX_POINTS = 8000;
/** Insert interpolated GPS samples when consecutive points are farther apart than this. */
const MAP_TRACK_MAX_GAP_M = 2.5;
/** A kart cannot leave the circuit by this much for one fix and immediately return. */
const GPS_ISOLATED_OUTLIER_LEG_M = 100;
const GPS_ISOLATED_OUTLIER_BRIDGE_M = 50;

export type TrackMapLayoutPoint = TrackMapPoint & {
  leftPct: number;
  topPct: number;
};

/** Project track points into 0–100 layout space for pseudo-map / RN percentage positioning. */
export function layoutTrackMapPoints(points: TrackMapPoint[]): TrackMapLayoutPoint[] {
  if (points.length === 0) {
    return [];
  }

  const lats = points.map((point) => point.lat);
  const lons = points.map((point) => point.lon);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);
  const latSpan = maxLat - minLat || 0.00001;
  const lonSpan = maxLon - minLon || 0.00001;

  return points.map((point) => ({
    ...point,
    leftPct: ((point.lon - minLon) / lonSpan) * 100,
    topPct: (1 - (point.lat - minLat) / latSpan) * 100,
  }));
}

export function interpolateTrackMapLayoutAtTime(
  points: TrackMapPoint[],
  timecodeMs: number,
): Pick<TrackMapLayoutPoint, 'leftPct' | 'topPct' | 'lat' | 'lon'> | null {
  const layoutBounds = layoutTrackMapPoints(points);
  if (layoutBounds.length === 0) {
    return null;
  }

  const interpolated = interpolateMapPointAtTime(points, timecodeMs);
  if (!interpolated) {
    return null;
  }

  const lats = points.map((point) => point.lat);
  const lons = points.map((point) => point.lon);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);
  const latSpan = maxLat - minLat || 0.00001;
  const lonSpan = maxLon - minLon || 0.00001;

  return {
    lat: interpolated.lat,
    lon: interpolated.lon,
    leftPct: ((interpolated.lon - minLon) / lonSpan) * 100,
    topPct: (1 - (interpolated.lat - minLat) / latSpan) * 100,
  };
}

type TrackProjection = {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
  lonScale: number;
  scale: number;
  offsetX: number;
  offsetY: number;
  viewBox: typeof TRACK_VIEWBOX;
};

function computeTrackProjection(
  boundsSource: MyChronGpsPoint[],
  viewBox = TRACK_VIEWBOX,
): TrackProjection | null {
  if (boundsSource.length === 0) {
    return null;
  }

  const lats = boundsSource.map((point) => point.lat);
  const lons = boundsSource.map((point) => point.lon);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);
  const latMid = (minLat + maxLat) / 2;
  const lonScale = Math.cos((latMid * Math.PI) / 180);
  const lonSpan = (maxLon - minLon) * lonScale || 0.00001;
  const latSpan = maxLat - minLat || 0.00001;
  const drawWidth = viewBox.width - viewBox.padding * 2;
  const drawHeight = viewBox.height - viewBox.padding * 2;
  const scale = Math.min(drawWidth / lonSpan, drawHeight / latSpan);
  const projectedWidth = lonSpan * scale;
  const projectedHeight = latSpan * scale;
  const offsetX = viewBox.padding + (drawWidth - projectedWidth) / 2;
  const offsetY = viewBox.padding + (drawHeight - projectedHeight) / 2;

  return {
    minLat,
    maxLat,
    minLon,
    maxLon,
    lonScale,
    scale,
    offsetX,
    offsetY,
    viewBox,
  };
}

function downsampleGpsForMap(points: MyChronGpsPoint[], maxPoints: number): MyChronGpsPoint[] {
  if (points.length <= maxPoints) {
    return points;
  }

  const targetBuckets = Math.max(1, Math.floor(maxPoints / 2));
  const bucketSize = points.length / targetBuckets;
  const result: MyChronGpsPoint[] = [];

  for (let bucketIndex = 0; bucketIndex < targetBuckets; bucketIndex += 1) {
    const start = Math.floor(bucketIndex * bucketSize);
    const end = Math.min(points.length, Math.floor((bucketIndex + 1) * bucketSize));
    const bucket = points.slice(start, end);
    if (bucket.length === 0) {
      continue;
    }

    const first = bucket[0]!;
    const last = bucket.at(-1)!;
    result.push(first);

    if (bucket.length >= 3) {
      let best = bucket[1]!;
      let bestScore = 0;
      for (let index = 1; index < bucket.length - 1; index += 1) {
        const candidate = bucket[index]!;
        const score =
          Math.abs(candidate.lat - first.lat) +
          Math.abs(candidate.lon - first.lon) +
          Math.abs(candidate.lat - last.lat) +
          Math.abs(candidate.lon - last.lon);
        if (score > bestScore) {
          bestScore = score;
          best = candidate;
        }
      }
      if (best !== first && best !== last) {
        result.push(best);
      }
    }

    if (last !== first) {
      result.push(last);
    }
  }

  if (result.length > maxPoints) {
    return downsampleTrackPoints(result, maxPoints);
  }

  return result.length >= 2 ? result : points.slice(0, Math.min(points.length, maxPoints));
}

function fillGpsGapsForMap(
  points: MyChronGpsPoint[],
  maxGapM: number,
  maxPoints: number,
): MyChronGpsPoint[] {
  if (points.length < 2 || maxGapM <= 0) {
    return points;
  }

  const result: MyChronGpsPoint[] = [points[0]!];

  for (let index = 1; index < points.length; index += 1) {
    const segmentStart = result.at(-1)!;
    const segmentEnd = points[index]!;
    const distanceM = haversineMeters(segmentStart, segmentEnd);

    if (distanceM > maxGapM) {
      const segmentCount = Math.ceil(distanceM / maxGapM);
      for (let step = 1; step < segmentCount; step += 1) {
        if (result.length >= maxPoints - 1) {
          break;
        }

        const progress = step / segmentCount;
        result.push(interpolateGpsSegment([segmentStart, segmentEnd], progress));
      }
    }

    if (result.length >= maxPoints) {
      break;
    }

    result.push(segmentEnd);
  }

  if (result.length > maxPoints) {
    return downsampleGpsForMap(result, maxPoints);
  }

  return result;
}

function mapGpsThroughProjection(
  points: MyChronGpsPoint[],
  projection: TrackProjection,
  maxPoints: number,
): TrackMapPoint[] {
  const filled = fillGpsGapsForMap(points, MAP_TRACK_MAX_GAP_M, maxPoints);
  const sampled = downsampleGpsForMap(filled, maxPoints);

  return sampled.map((point) => ({
    x:
      projection.offsetX + (point.lon - projection.minLon) * projection.lonScale * projection.scale,
    y:
      projection.viewBox.height -
      projection.offsetY -
      (point.lat - projection.minLat) * projection.scale,
    lat: point.lat,
    lon: point.lon,
    speedKph: point.speed * 3.6,
    timecodeMs: point.timecodeMs,
  }));
}

/**
 * Remove isolated bad fixes without trimming legitimate paddock or in/out-lap driving.
 *
 * AIM loggers can occasionally emit one geographically valid but wildly misplaced ECEF fix.
 * Connecting every fix as one line produces the long out-and-back "teleport" seen on maps.
 * A point is only removed when both legs are implausibly long and its neighbours remain close,
 * so a sustained move to another location is preserved.
 */
export function filterIsolatedGpsOutliers(points: MyChronGpsPoint[]): MyChronGpsPoint[] {
  if (points.length < 3) {
    return points;
  }

  const filtered = points.filter((point) => (
    Number.isFinite(point.lat)
    && Number.isFinite(point.lon)
    && Math.abs(point.lat) <= 90
    && Math.abs(point.lon) <= 180
    && !(point.lat === 0 && point.lon === 0)
    && (point.fix === undefined || point.fix >= 2)
  ));

  if (filtered.length < 3) {
    return filtered;
  }

  const result = [filtered[0]!];
  for (let index = 1; index < filtered.length - 1;) {
    const previous = result.at(-1)!;
    let skipped = false;

    // A logger glitch is normally one fix, but occasionally arrives as a very short burst.
    for (let burstLength = 1; burstLength <= 3; burstLength += 1) {
      const lastBadIndex = index + burstLength - 1;
      const followingIndex = lastBadIndex + 1;
      if (followingIndex >= filtered.length) {
        break;
      }

      const firstBad = filtered[index]!;
      const lastBad = filtered[lastBadIndex]!;
      const following = filtered[followingIndex]!;
      if (
        haversineMeters(previous, firstBad) >= GPS_ISOLATED_OUTLIER_LEG_M
        && haversineMeters(lastBad, following) >= GPS_ISOLATED_OUTLIER_LEG_M
        && haversineMeters(previous, following) <= GPS_ISOLATED_OUTLIER_BRIDGE_M
      ) {
        index = followingIndex;
        skipped = true;
        break;
      }
    }

    if (!skipped) {
      result.push(filtered[index]!);
      index += 1;
    }
  }

  result.push(filtered.at(-1)!);
  return result;
}

export function projectTrackPoints(
  points: MyChronGpsPoint[],
  maxPoints = 600,
  viewBox = TRACK_VIEWBOX,
): TrackMapPoint[] {
  const displayPoints = filterIsolatedGpsOutliers(points);
  const projection = computeTrackProjection(displayPoints, viewBox);
  if (!projection) {
    return [];
  }

  return mapGpsThroughProjection(displayPoints, projection, maxPoints);
}

export function projectTrackPointsWithReferenceBounds(
  points: MyChronGpsPoint[],
  boundsReference: MyChronGpsPoint[],
  maxPoints = 600,
  viewBox = TRACK_VIEWBOX,
): TrackMapPoint[] {
  const displayPoints = filterIsolatedGpsOutliers(points);
  const displayBounds = filterIsolatedGpsOutliers(boundsReference);
  const projection = computeTrackProjection(displayBounds, viewBox);
  if (!projection || displayPoints.length === 0) {
    return [];
  }

  return mapGpsThroughProjection(displayPoints, projection, maxPoints);
}

export function speedColorBrakeToFlatOut(speedKph: number, minSpeed: number, maxSpeed: number) {
  const range = maxSpeed - minSpeed || 1;
  const ratio = Math.min(1, Math.max(0, (speedKph - minSpeed) / range));
  return interpolateSpeedLegendColor(ratio);
}

const SPEED_LEGEND_STOPS: Array<{ stop: number; rgb: readonly [number, number, number] }> = [
  { stop: 0, rgb: [255, 45, 45] },
  { stop: 0.18, rgb: [255, 102, 0] },
  { stop: 0.36, rgb: [255, 187, 0] },
  { stop: 0.54, rgb: [221, 255, 0] },
  { stop: 0.72, rgb: [102, 255, 102] },
  { stop: 1, rgb: [0, 255, 153] },
];

function interpolateSpeedLegendColor(ratio: number) {
  const clamped = Math.min(1, Math.max(0, ratio));
  let lower = SPEED_LEGEND_STOPS[0]!;
  let upper = SPEED_LEGEND_STOPS.at(-1)!;

  for (let index = 0; index < SPEED_LEGEND_STOPS.length - 1; index += 1) {
    const start = SPEED_LEGEND_STOPS[index]!;
    const end = SPEED_LEGEND_STOPS[index + 1]!;
    if (clamped >= start.stop && clamped <= end.stop) {
      lower = start;
      upper = end;
      break;
    }
  }

  const span = upper.stop - lower.stop || 1;
  const t = (clamped - lower.stop) / span;
  const r = Math.round(lower.rgb[0] + (upper.rgb[0] - lower.rgb[0]) * t);
  const g = Math.round(lower.rgb[1] + (upper.rgb[1] - lower.rgb[1]) * t);
  const b = Math.round(lower.rgb[2] + (upper.rgb[2] - lower.rgb[2]) * t);

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`.toUpperCase();
}

export function speedColor(speedKph: number, minSpeed: number, maxSpeed: number) {
  return speedColorBrakeToFlatOut(speedKph, minSpeed, maxSpeed);
}

export function trackSpeedRange(points: TrackMapPoint[]) {
  if (points.length === 0) {
    return null;
  }

  const speeds = points.map((point) => point.speedKph).sort((left, right) => left - right);
  const lowIndex = Math.max(0, Math.floor(speeds.length * 0.06));
  const highIndex = Math.min(speeds.length - 1, Math.ceil(speeds.length * 0.94) - 1);
  const min = speeds[lowIndex]!;
  const max = speeds[highIndex]!;

  if (max - min < 4) {
    return { min: Math.max(0, min - 2), max: max + 2 };
  }

  return { min, max };
}

export function analyzeLap(
  session: MyChronSessionExport,
  targetLapNumber: number,
  exclusions: LapExclusion[] = [],
  options?: {
    windowOverride?: Pick<ReplayLapWindow, 'startMs' | 'endMs' | 'durationSec'>;
    rankPool?: Array<{ lapNumber: number; durationSec: number }>;
  },
) {
  const laps = racingLaps(session.laps);
  const rawLaps = resolveRawLoggerLaps(session);
  const activeLaps = racingLapsForAnalysis(session, exclusions);
  const lapIndex = laps.findIndex((lap) => lap.lapNumber === targetLapNumber);
  const rawLapIndex = rawLaps.findIndex((lap) => lap.lapNumber === targetLapNumber);
  if (lapIndex < 0 && rawLapIndex < 0 && !options?.windowOverride) {
    return null;
  }

  const selectedLap =
    lapIndex >= 0 ? laps[lapIndex]! : rawLapIndex >= 0 ? rawLaps[rawLapIndex]! : null;
  const startMs = options?.windowOverride?.startMs ?? lapWindow(selectedLap ?? rawLaps[0]!).startMs;
  const endMs = options?.windowOverride?.endMs ?? lapWindow(selectedLap ?? rawLaps[0]!).endMs;
  const durationSec =
    options?.windowOverride?.durationSec ?? (selectedLap ?? rawLaps[0]!).durationMs / 1000;
  const lap: MyChronLapRecord = selectedLap
    ? selectedLap
    : {
        segment: 0,
        lapNumber: targetLapNumber,
        durationMs: durationSec * 1000,
        endTimeMs: endMs,
      };
  const rankPool = options?.rankPool;
  const ranked = rankPool?.length
    ? [...rankPool].sort((left, right) => left.durationSec - right.durationSec)
    : [...activeLaps].sort((left, right) => left.durationMs - right.durationMs);
  const bestEntry = ranked[0] ?? null;
  const bestLap =
    bestEntry && rankPool?.length
      ? (activeLaps.find((entry) => entry.lapNumber === bestEntry.lapNumber) ?? lap)
      : (ranked[0] ?? lap);
  const rank = rankPool?.length
    ? ranked.findIndex((entry) => entry.lapNumber === targetLapNumber) + 1
    : activeLaps.some((entry) => entry.lapNumber === lap.lapNumber)
      ? ranked.findIndex((entry) => entry.lapNumber === lap.lapNumber) + 1
      : laps.length;
  const lapGps = gpsInWindow(session.gps, startMs, endMs);
  const speeds = lapGps.map((point) => point.speed * 3.6);
  const bestDurationSec = rankPool?.length
    ? Math.min(...rankPool.map((entry) => entry.durationSec))
    : (bestLap as MyChronLapRecord).durationMs / 1000;

  return {
    lap:
      lapIndex >= 0 ? lap : { ...lap, lapNumber: targetLapNumber, durationMs: durationSec * 1000 },
    lapIndex: lapIndex >= 0 ? lapIndex : 0,
    startMs,
    endMs,
    durationSec,
    rank: rank > 0 ? rank : laps.length,
    totalLaps: rankPool?.length ?? activeLaps.length,
    gapToBestSec:
      bestEntry && targetLapNumber !== bestEntry.lapNumber
        ? durationSec - bestDurationSec
        : targetLapNumber === bestEntry?.lapNumber
          ? null
          : null,
    idealLapSec: null,
    topSpeedKph: speeds.length ? Math.max(...speeds) : null,
    averageSpeedKph: speeds.length
      ? speeds.reduce((sum, value) => sum + value, 0) / speeds.length
      : null,
    maxRpm: maxInWindow(session.series.RPM, startMs, endMs),
    maxTempC: maxInWindow(session.series['TC 1'] ?? session.series['TC 2'], startMs, endMs),
    accXBaseline: medianInWindow(session.series.AccX, startMs, endMs),
    mapPoints: buildMapPointsForGpsWindow(session, startMs, endMs, MAP_TRACK_MAX_POINTS),
  };
}

function formatOverviewReadoutValue(
  channel: (typeof OVERVIEW_CHANNELS)[number],
  shortName: string,
  value: number,
  unit: string | null,
  preferences: MeasurementPreferences,
) {
  if (channel.label === 'RPM') {
    return `${Math.round(value).toLocaleString()} rpm`;
  }

  if (channel.label === 'Steering') {
    return `${value.toFixed(0)} °`;
  }

  if (channel.label === 'Lateral G' || channel.label === 'Longitudinal G') {
    return `${value.toFixed(2)} G`;
  }

  if (channel.label === 'Battery') {
    return `${value.toFixed(1)} ${unit ?? 'V'}`;
  }

  return formatOverviewChannelValue(channel.label, value, unit, preferences);
}

export function buildChannelReadouts(
  session: MyChronSessionExport,
  analysis: LapAnalysis,
  preferences: MeasurementPreferences = getMeasurementPreferences(),
): ChannelReadout[] {
  const { startMs, endMs } = analysis;

  return OVERVIEW_CHANNELS.map((channel) => {
    const shortName = channel.shortNames.find((name) => session.series[name]?.length);
    if (!shortName) {
      return {
        label: channel.label,
        shortName: channel.shortNames[0] ?? channel.label,
        value: 'N/A',
        color: channel.color,
        enabled: false,
      };
    }

    const unit = channelUnit(session.channels, shortName);
    const rawValue =
      latestInWindow(session.series[shortName], startMs, endMs) ??
      maxInWindow(session.series[shortName], startMs, endMs);
    const value =
      shortName === 'AccX'
        ? driverLongitudinalG(rawValue, analysis.accXBaseline)
        : shortName === 'AccY'
          ? driverLateralG(rawValue)
          : rawValue;

    return {
      label: channel.label,
      shortName,
      value:
        value === null
          ? 'N/A'
          : formatOverviewReadoutValue(channel, shortName, value, unit, preferences),
      color: channel.color,
      enabled: value !== null,
    };
  });
}

export function buildChannelReadoutsAtTime(
  session: MyChronSessionExport,
  timecodeMs: number,
  accXBaseline: number | null = null,
  preferences: MeasurementPreferences = getMeasurementPreferences(),
): ChannelReadout[] {
  return OVERVIEW_CHANNELS.map((channel) => {
    const shortName = channel.shortNames.find((name) => session.series[name]?.length);
    if (!shortName) {
      return {
        label: channel.label,
        shortName: channel.shortNames[0] ?? channel.label,
        value: 'N/A',
        color: channel.color,
        enabled: false,
      };
    }

    const unit = channelUnit(session.channels, shortName);
    const rawValue =
      channel.label === 'Speed'
        ? speedKphAtTime(session, timecodeMs)
        : interpolatedSeriesValue(session.series[shortName], timecodeMs, 3000);
    const value =
      shortName === 'AccX'
        ? driverLongitudinalG(rawValue, accXBaseline)
        : shortName === 'AccY'
          ? driverLateralG(rawValue)
          : rawValue;

    return {
      label: channel.label,
      shortName,
      value:
        value === null
          ? 'N/A'
          : formatOverviewReadoutValue(channel, shortName, value, unit, preferences),
      color: channel.color,
      enabled: value !== null,
    };
  });
}

export function defaultLapNumber(session: MyChronSessionExport, exclusions: LapExclusion[] = []) {
  const laps = racingLapsForAnalysis(session, exclusions);
  if (laps.length === 0) {
    return null;
  }

  return [...laps].sort((left, right) => left.durationMs - right.durationMs)[0]!.lapNumber;
}

export function defaultCompareLapNumber(
  session: MyChronSessionExport,
  selectedLapNumber: number,
  exclusions: LapExclusion[] = [],
) {
  const laps = racingLapsForAnalysis(session, exclusions)
    .filter((lap) => lap.lapNumber !== selectedLapNumber)
    .sort((left, right) => left.durationMs - right.durationMs);

  return laps[0]?.lapNumber ?? null;
}

export type LapTracePoint = {
  index: number;
  elapsedSec: number;
  timecodeMs: number;
  speed: number | null;
  rpm: number | null;
  steering: number | null;
  accX: number | null;
  accY: number | null;
};

export type SectorTimesSec = {
  s1: number;
  s2: number;
  s3: number;
};

export type LapTableRow = {
  lapNumber: number;
  timeSec: number;
  gapSec: number | null;
  sectors: SectorTimesSec | null;
  topSpeedKph: number | null;
  isBest: boolean;
  excluded?: boolean;
  exclusionReason?: LapExclusionReason | null;
};

export type SectorComparison = {
  thisLap: SectorTimesSec;
  bestLap: SectorTimesSec;
  idealLap: SectorTimesSec;
  gap: SectorTimesSec;
  bestLapNumber: number;
  bestSectorLapNumbers: SectorTimesSec;
};

export type SessionAnalysis = {
  bestLapNumber: number;
  sectorBoundaryFractions: [number, number];
  lapRows: LapTableRow[];
  idealLapSec: number | null;
  hasRecordedSectorTimes: boolean;
};

const EARTH_RADIUS_M = 6_371_000;

function haversineMeters(a: MyChronGpsPoint, b: MyChronGpsPoint) {
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const dLat = lat2 - lat1;
  const dLon = ((b.lon - a.lon) * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
}

function nearestSeriesValue(
  series: MyChronSeriesSample[] | undefined,
  timecodeMs: number,
  thresholdMs = 250,
) {
  if (!series?.length) {
    return null;
  }

  let closest = series[0]!;
  let closestDelta = Math.abs(closest.timecodeMs - timecodeMs);

  for (const sample of series) {
    const delta = Math.abs(sample.timecodeMs - timecodeMs);
    if (delta < closestDelta) {
      closest = sample;
      closestDelta = delta;
    }
  }

  return closestDelta <= thresholdMs ? closest.value : null;
}

function interpolatedSeriesValue(
  series: MyChronSeriesSample[] | undefined,
  timecodeMs: number,
  maxGapMs = 1500,
) {
  if (!series?.length) {
    return null;
  }

  if (series.length === 1) {
    return Math.abs(series[0]!.timecodeMs - timecodeMs) <= maxGapMs ? series[0]!.value : null;
  }

  const first = series[0]!;
  if (timecodeMs <= first.timecodeMs) {
    return first.timecodeMs - timecodeMs <= maxGapMs ? first.value : null;
  }

  const last = series.at(-1)!;
  if (timecodeMs >= last.timecodeMs) {
    return timecodeMs - last.timecodeMs <= maxGapMs ? last.value : null;
  }

  let lo = 1;
  let hi = series.length - 1;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (series[mid]!.timecodeMs < timecodeMs) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }

  const next = series[lo]!;
  const prev = series[lo - 1]!;
  const span = next.timecodeMs - prev.timecodeMs;
  if (span <= 0 || span > maxGapMs) {
    return nearestSeriesValue(series, timecodeMs, maxGapMs);
  }

  const ratio = (timecodeMs - prev.timecodeMs) / span;
  return prev.value + (next.value - prev.value) * ratio;
}

function interpolatedSeriesValueWithHint(
  series: MyChronSeriesSample[] | undefined,
  timecodeMs: number,
  hint?: InterpolateMapPointHint,
  maxGapMs = 1500,
) {
  if (!series?.length) {
    return null;
  }

  if (series.length === 1) {
    if (hint) {
      hint.index = 0;
    }
    return Math.abs(series[0]!.timecodeMs - timecodeMs) <= maxGapMs ? series[0]!.value : null;
  }

  const first = series[0]!;
  if (timecodeMs <= first.timecodeMs) {
    if (hint) {
      hint.index = 0;
    }
    return first.timecodeMs - timecodeMs <= maxGapMs ? first.value : null;
  }

  const last = series.at(-1)!;
  if (timecodeMs >= last.timecodeMs) {
    if (hint) {
      hint.index = Math.max(0, series.length - 2);
    }
    return timecodeMs - last.timecodeMs <= maxGapMs ? last.value : null;
  }

  let lo = hint ? Math.max(0, Math.min(hint.index, series.length - 2)) : 0;

  if (series[lo + 1]!.timecodeMs < timecodeMs) {
    while (lo < series.length - 2 && series[lo + 1]!.timecodeMs < timecodeMs) {
      lo += 1;
    }
  } else {
    while (lo > 0 && series[lo]!.timecodeMs > timecodeMs) {
      lo -= 1;
    }
  }

  while (lo < series.length - 2 && series[lo + 1]!.timecodeMs < timecodeMs) {
    lo += 1;
  }
  while (lo > 0 && series[lo]!.timecodeMs > timecodeMs) {
    lo -= 1;
  }

  if (hint) {
    hint.index = lo;
  }

  const next = series[lo + 1]!;
  const prev = series[lo]!;
  const span = next.timecodeMs - prev.timecodeMs;
  if (span <= 0 || span > maxGapMs) {
    return nearestSeriesValue(series, timecodeMs, maxGapMs);
  }

  const ratio = (timecodeMs - prev.timecodeMs) / span;
  return prev.value + (next.value - prev.value) * ratio;
}

function interpolateGpsSpeedMs(gps: MyChronGpsPoint[], timecodeMs: number): number | null {
  if (gps.length === 0) {
    return null;
  }

  if (gps.length === 1) {
    return gps[0]!.speed;
  }

  const first = gps[0]!;
  if (timecodeMs <= first.timecodeMs) {
    return first.speed;
  }

  const last = gps.at(-1)!;
  if (timecodeMs >= last.timecodeMs) {
    return last.speed;
  }

  let lo = 1;
  let hi = gps.length - 1;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (gps[mid]!.timecodeMs < timecodeMs) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }

  const next = gps[lo]!;
  const prev = gps[lo - 1]!;
  const span = next.timecodeMs - prev.timecodeMs;
  const t = span === 0 ? 0 : (timecodeMs - prev.timecodeMs) / span;

  return prev.speed + (next.speed - prev.speed) * t;
}

/** Wheel speed when available; otherwise interpolated GPS ground speed. */
export function speedKphAtTime(session: MyChronSessionExport, timecodeMs: number): number | null {
  const wheelKph = interpolatedSeriesValue(session.series.WSpd, timecodeMs);
  if (wheelKph !== null && wheelKph > 0.5) {
    return wheelKph;
  }

  const gpsSpeedMs = interpolateGpsSpeedMs(session.gps, timecodeMs);
  if (gpsSpeedMs === null) {
    return wheelKph;
  }

  return gpsSpeedMs * 3.6;
}

function enrichGpsWithDisplaySpeed(session: MyChronSessionExport, gps: MyChronGpsPoint[]) {
  return gps.map((point) => {
    const kph = speedKphAtTime(session, point.timecodeMs);
    if (kph === null) {
      return point;
    }

    return { ...point, speed: kph / 3.6 };
  });
}

export function buildMapPointsForGpsWindow(
  session: MyChronSessionExport,
  startMs: number,
  endMs: number,
  maxPoints = MAP_TRACK_MAX_POINTS,
  boundsReference?: MyChronGpsPoint[],
) {
  const gps = enrichGpsWithDisplaySpeed(session, sliceGpsAtTimeBounds(session.gps, startMs, endMs));
  if (boundsReference?.length) {
    return projectTrackPointsWithReferenceBounds(gps, boundsReference, maxPoints);
  }

  return projectTrackPoints(gps, maxPoints);
}

export function buildSessionMapPoints(session: MyChronSessionExport, timeline: ReplayTimeline) {
  const boundsGps = gpsInWindow(session.gps, timeline.startTimecodeMs, timeline.endTimecodeMs);

  return buildMapPointsForGpsWindow(
    session,
    timeline.startTimecodeMs,
    timeline.endTimecodeMs,
    MAP_TRACK_MAX_POINTS * 3,
    boundsGps,
  );
}

export function splitTrackGroupsByLapWindows(points: TrackMapPoint[], timeline: ReplayTimeline) {
  return timeline.laps
    .map((lap) =>
      points.filter((point) => point.timecodeMs >= lap.startMs && point.timecodeMs <= lap.endMs),
    )
    .filter((group) => group.length >= 2);
}

export function flattenTrackPointGroups(groups: TrackMapPoint[][]) {
  return groups.flat();
}

function sectorBoundaryFractions(
  gps: MyChronGpsPoint[],
  startMs: number,
  endMs: number,
): [number, number] | null {
  const boundaries = gpsDistanceSectorBoundaries(gps, startMs, endMs);
  if (!boundaries) {
    return null;
  }

  const duration = endMs - startMs;
  if (duration <= 0) {
    return null;
  }

  return [(boundaries[0] - startMs) / duration, (boundaries[1] - startMs) / duration];
}

function gpsDistanceSectorBoundaries(
  gps: MyChronGpsPoint[],
  startMs: number,
  endMs: number,
): [number, number] | null {
  if (gps.length < 3) {
    return null;
  }

  const cumulative = [0];
  for (let index = 1; index < gps.length; index += 1) {
    cumulative.push(cumulative[index - 1]! + haversineMeters(gps[index - 1]!, gps[index]!));
  }

  const total = cumulative.at(-1) ?? 0;
  if (total <= 0 || endMs <= startMs) {
    return null;
  }

  const targets = [total / 3, (2 * total) / 3];
  const boundaries: number[] = [];

  for (const target of targets) {
    let boundaryMs = endMs;
    for (let index = 1; index < cumulative.length; index += 1) {
      if (cumulative[index]! >= target) {
        boundaryMs = gps[index]!.timecodeMs;
        break;
      }
    }
    boundaries.push(boundaryMs);
  }

  return [boundaries[0]!, boundaries[1]!];
}

export function computeSectorTimes(
  lap: MyChronLapRecord,
  boundaryFractions: [number, number],
): SectorTimesSec {
  const { startMs, endMs } = lapWindow(lap);
  const duration = endMs - startMs;
  const boundary1 = startMs + duration * boundaryFractions[0];
  const boundary2 = startMs + duration * boundaryFractions[1];

  return {
    s1: (boundary1 - startMs) / 1000,
    s2: (boundary2 - boundary1) / 1000,
    s3: (endMs - boundary2) / 1000,
  };
}

export function getLapGpsSectorWindows(
  session: MyChronSessionExport,
  lap: MyChronLapRecord,
): Array<[number, number]> {
  const { startMs, endMs } = lapWindow(lap);
  const lapGps = gpsInWindow(session.gps, startMs, endMs);
  const boundaries = gpsDistanceSectorBoundaries(lapGps, startMs, endMs);

  if (!boundaries) {
    return [[startMs, endMs]];
  }

  const [boundary1, boundary2] = boundaries;
  return [
    [startMs, boundary1],
    [boundary1, boundary2],
    [boundary2, endMs],
  ];
}

export function computeLapGpsSectorTimes(
  session: MyChronSessionExport,
  lap: MyChronLapRecord,
): SectorTimesSec {
  const { startMs, endMs } = lapWindow(lap);
  const windows = getLapGpsSectorWindows(session, lap);

  if (windows.length !== 3) {
    const durationSec = (endMs - startMs) / 1000;
    return { s1: durationSec, s2: 0, s3: 0 };
  }

  return {
    s1: (windows[0]![1] - windows[0]![0]) / 1000,
    s2: (windows[1]![1] - windows[1]![0]) / 1000,
    s3: (windows[2]![1] - windows[2]![0]) / 1000,
  };
}

export function recordedSectorTimesForLap(
  session: MyChronSessionExport,
  lapNumber: number,
): SectorTimesSec | null {
  const sectorRecords = session.laps
    .filter((lap) => lap.lapNumber === lapNumber && lap.segment > 0)
    .sort((left, right) => left.segment - right.segment);

  if (sectorRecords.length < 3) {
    return null;
  }

  return {
    s1: sectorRecords[0]!.durationMs / 1000,
    s2: sectorRecords[1]!.durationMs / 1000,
    s3: sectorRecords[2]!.durationMs / 1000,
  };
}

export function analyzeSession(
  session: MyChronSessionExport,
  exclusions: LapExclusion[] = [],
): SessionAnalysis | null {
  const laps = racingLapsForAnalysis(session, exclusions);
  if (laps.length === 0) {
    return null;
  }

  const bestLap = [...laps].sort((left, right) => left.durationMs - right.durationMs)[0]!;
  const bestWindow = lapWindow(bestLap);
  const bestGps = gpsInWindow(session.gps, bestWindow.startMs, bestWindow.endMs);
  const boundaryFractions =
    sectorBoundaryFractions(bestGps, bestWindow.startMs, bestWindow.endMs) ??
    ([1 / 3, 2 / 3] as [number, number]);

  const lapRows = laps.map((lap) => {
    const sectors = recordedSectorTimesForLap(session, lap.lapNumber);
    const window = lapWindow(lap);
    const speeds = gpsInWindow(session.gps, window.startMs, window.endMs).map(
      (point) => point.speed * 3.6,
    );

    return {
      lapNumber: lap.lapNumber,
      timeSec: lap.durationMs / 1000,
      gapSec:
        lap.lapNumber === bestLap.lapNumber ? null : (lap.durationMs - bestLap.durationMs) / 1000,
      sectors,
      topSpeedKph: speeds.length ? Math.max(...speeds) : null,
      isBest: lap.lapNumber === bestLap.lapNumber,
    };
  });

  const sectorRows = lapRows.filter(
    (row): row is LapTableRow & { sectors: SectorTimesSec } => row.sectors !== null,
  );
  const idealLapSec =
    sectorRows.length > 0
      ? ['s1', 's2', 's3'].reduce((sum, key) => {
          const bestSector = Math.min(
            ...sectorRows.map((row) => row.sectors[key as keyof SectorTimesSec]),
          );
          return sum + bestSector;
        }, 0)
      : null;

  return {
    bestLapNumber: bestLap.lapNumber,
    sectorBoundaryFractions: boundaryFractions,
    lapRows,
    idealLapSec,
    hasRecordedSectorTimes: sectorRows.length > 0,
  };
}

export function buildLapManagementRows(
  session: MyChronSessionExport,
  exclusions: LapExclusion[] = [],
): LapTableRow[] {
  const analysis = analyzeSession(session, exclusions);
  const allLaps = racingLaps(session.laps);
  if (allLaps.length === 0) {
    return [];
  }

  const excludedMap = new Map(exclusions.map((entry) => [entry.lapNumber, entry.reason]));
  const bestLap = analysis
    ? (allLaps.find((lap) => lap.lapNumber === analysis.bestLapNumber) ?? null)
    : null;

  return allLaps.map((lap) => {
    const excluded = excludedMap.has(lap.lapNumber);
    const sectors = recordedSectorTimesForLap(session, lap.lapNumber);
    const window = lapWindow(lap);
    const speeds = gpsInWindow(session.gps, window.startMs, window.endMs).map(
      (point) => point.speed * 3.6,
    );

    return {
      lapNumber: lap.lapNumber,
      timeSec: lap.durationMs / 1000,
      gapSec:
        !excluded && bestLap && lap.lapNumber !== bestLap.lapNumber
          ? (lap.durationMs - bestLap.durationMs) / 1000
          : null,
      sectors,
      topSpeedKph: speeds.length ? Math.max(...speeds) : null,
      isBest: Boolean(!excluded && analysis && lap.lapNumber === analysis.bestLapNumber),
      excluded,
      exclusionReason: excludedMap.get(lap.lapNumber) ?? null,
    };
  });
}

export function buildSectorComparison(
  session: MyChronSessionExport,
  lapNumber: number,
  sessionAnalysis: SessionAnalysis,
  exclusions: LapExclusion[] = [],
): SectorComparison | null {
  const laps = racingLapsForAnalysis(session, exclusions);
  const selected = laps.find((lap) => lap.lapNumber === lapNumber);
  const best = laps.find((lap) => lap.lapNumber === sessionAnalysis.bestLapNumber);
  if (!selected || !best) {
    return null;
  }

  const thisLapSectors = recordedSectorTimesForLap(session, selected.lapNumber);
  const bestLapSectors = recordedSectorTimesForLap(session, best.lapNumber);
  const sectorRows = sessionAnalysis.lapRows.filter(
    (row): row is LapTableRow & { sectors: SectorTimesSec } => row.sectors !== null,
  );
  if (!thisLapSectors || !bestLapSectors || sectorRows.length === 0) {
    return null;
  }

  const idealLap = {
    s1: Math.min(...sectorRows.map((row) => row.sectors.s1)),
    s2: Math.min(...sectorRows.map((row) => row.sectors.s2)),
    s3: Math.min(...sectorRows.map((row) => row.sectors.s3)),
  };
  const bestSectorLaps = bestSectorLapNumbers(sessionAnalysis);
  if (!bestSectorLaps) {
    return null;
  }

  return {
    thisLap: thisLapSectors,
    bestLap: bestLapSectors,
    idealLap,
    gap: {
      s1: thisLapSectors.s1 - idealLap.s1,
      s2: thisLapSectors.s2 - idealLap.s2,
      s3: thisLapSectors.s3 - idealLap.s3,
    },
    bestLapNumber: best.lapNumber,
    bestSectorLapNumbers: {
      s1: bestSectorLaps.s1,
      s2: bestSectorLaps.s2,
      s3: bestSectorLaps.s3,
    },
  };
}

const LAP_TRACE_MIN_ZOOM_SPAN_SEC = 0.25;

const TRACE_CHANNEL_SERIES = {
  speed: 'WSpd',
  rpm: 'RPM',
  steering: 'StrA',
  accY: 'AccY',
  accX: 'AccX',
} as const;

export type TraceChannelSeriesKey = keyof typeof TRACE_CHANNEL_SERIES;

export type SeriesTracePoint = {
  index: number;
  elapsedSec: number;
  timecodeMs: number;
  value: number | null;
};

export type SessionTraceChannel = {
  shortName: string;
  longName: string;
  unit: string;
  decimals: number;
  color: string;
  sampleCount: number;
};

const KNOWN_TRACE_COLORS: Record<string, string> = {
  WSpd: '#60A5FA',
  RPM: '#EF4444',
  StrA: '#A855F7',
  AccY: '#22D3EE',
  AccX: '#FACC15',
  LogT: '#6366F1',
  'TC 1': '#22C55E',
  'TC 2': '#10B981',
  VBat: '#F97316',
  VBIn: '#FB923C',
};

const TRACE_COLOR_PALETTE = [
  '#60A5FA',
  '#EF4444',
  '#A855F7',
  '#22D3EE',
  '#FACC15',
  '#22C55E',
  '#F97316',
  '#EC4899',
  '#14B8A6',
  '#8B5CF6',
  '#84CC16',
  '#06B6D4',
];

const TRACE_CHANNEL_PRIORITY = [
  'WSpd',
  'RPM',
  'StrA',
  'AccY',
  'AccX',
  'LogT',
  'TC 1',
  'TC 2',
  'VBat',
  'VBIn',
];

function traceColorForShortName(shortName: string, index: number) {
  return KNOWN_TRACE_COLORS[shortName] ?? TRACE_COLOR_PALETTE[index % TRACE_COLOR_PALETTE.length]!;
}

function transformSeriesSampleValue(
  shortName: string,
  raw: number,
  accXBaseline: number | null,
): number {
  if (shortName === 'AccX') {
    return driverLongitudinalG(raw, accXBaseline) ?? raw;
  }

  if (shortName === 'AccY') {
    return driverLateralG(raw) ?? raw;
  }

  return raw;
}

function listTraceChannels(
  session: MyChronSessionExport,
  startMs: number,
  endMs: number,
  includeEmpty: boolean,
): SessionTraceChannel[] {
  const channels: SessionTraceChannel[] = [];
  const seen = new Set<string>();
  let paletteIndex = 0;

  function pushChannel(meta: MyChronChannelMeta) {
    if (seen.has(meta.shortName)) {
      return;
    }

    const samples = seriesInWindow(session.series[meta.shortName], startMs, endMs);
    if (!includeEmpty && samples.length === 0) {
      return;
    }

    seen.add(meta.shortName);
    channels.push({
      shortName: meta.shortName,
      longName: meta.longName || meta.shortName,
      unit: meta.unit ?? '',
      decimals: meta.decimals ?? 2,
      color: traceColorForShortName(meta.shortName, paletteIndex),
      sampleCount: samples.length,
    });
    paletteIndex += 1;
  }

  for (const meta of session.channels) {
    pushChannel(meta);
  }

  for (const shortName of Object.keys(session.series)) {
    if (seen.has(shortName)) {
      continue;
    }

    pushChannel({
      index: -1,
      shortName,
      longName: shortName,
      unit: channelUnit(session.channels, shortName) ?? '',
      decimals: 2,
    });
  }

  return channels.sort((left, right) => {
    const leftPriority = TRACE_CHANNEL_PRIORITY.indexOf(left.shortName);
    const rightPriority = TRACE_CHANNEL_PRIORITY.indexOf(right.shortName);
    const leftRank = leftPriority === -1 ? Number.MAX_SAFE_INTEGER : leftPriority;
    const rightRank = rightPriority === -1 ? Number.MAX_SAFE_INTEGER : rightPriority;
    if (leftRank !== rightRank) {
      return leftRank - rightRank;
    }

    return left.shortName.localeCompare(right.shortName);
  });
}

export function listPopulatedTraceChannels(
  session: MyChronSessionExport,
  startMs: number,
  endMs: number,
): SessionTraceChannel[] {
  return listTraceChannels(session, startMs, endMs, false);
}

export function listAllTraceChannels(
  session: MyChronSessionExport,
  startMs: number,
  endMs: number,
): SessionTraceChannel[] {
  return listTraceChannels(session, startMs, endMs, true);
}

export function defaultEnabledTraceChannels(
  channels: SessionTraceChannel[],
  compact = false,
): string[] {
  const preferred = compact ? ['WSpd', 'RPM'] : ['WSpd', 'RPM', 'StrA', 'AccY', 'AccX'];
  const picked = preferred.filter((shortName) =>
    channels.some((channel) => channel.shortName === shortName),
  );
  if (picked.length > 0) {
    return picked;
  }

  return channels.slice(0, compact ? 2 : 5).map((channel) => channel.shortName);
}

export function seriesValueAtTime(
  session: MyChronSessionExport,
  shortName: string,
  timecodeMs: number,
  accXBaseline: number | null = null,
): number | null {
  const raw = interpolatedSeriesValue(session.series[shortName], timecodeMs);
  if (raw === null) {
    return null;
  }

  return transformSeriesSampleValue(shortName, raw, accXBaseline);
}

export function formatSeriesTraceValue(
  shortName: string,
  value: number,
  unit: string,
  decimals: number,
  preferences: MeasurementPreferences = getMeasurementPreferences(),
): string {
  if (shortName === 'RPM') {
    return Math.round(value).toLocaleString();
  }

  if (shortName === 'StrA') {
    return `${value.toFixed(decimals)} °`;
  }

  if (shortName === 'AccX' || shortName === 'AccY') {
    return `${value.toFixed(decimals)} G`;
  }

  return formatTelemetrySeriesValue(shortName, value, unit, decimals, preferences);
}

export function autoSeriesTraceDomain(
  trace: SeriesTracePoint[],
  shortName: string,
): [number, number] {
  const values = trace
    .map((point) => point.value)
    .filter((value): value is number => value !== null && Number.isFinite(value));

  if (values.length === 0) {
    return shortName === 'RPM' ? [0, 16000] : [0, 1];
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = Math.max(max - min, 0);
  const padding =
    shortName === 'RPM'
      ? Math.max(span * 0.12, 250)
      : Math.max(span * 0.08, span === 0 ? Math.abs(max) * 0.1 || 1 : 0);

  if (shortName === 'RPM') {
    return [Math.max(0, Math.floor(min - padding)), Math.ceil(max + padding)];
  }

  if (span === 0) {
    const pad = Math.abs(max) * 0.1 || 1;
    return [min - pad, max + pad];
  }

  return [min - padding, max + padding];
}

export function buildGenericSeriesTrace(
  session: MyChronSessionExport,
  shortName: string,
  startMs: number,
  endMs: number,
  elapsedOriginMs: number,
  maxPoints?: number,
  accXBaseline: number | null = null,
): SeriesTracePoint[] {
  const series = seriesInWindow(session.series[shortName], startMs, endMs);
  if (!series.length) {
    return [];
  }

  const sampled =
    maxPoints !== undefined && series.length > maxPoints
      ? shortName === 'RPM'
        ? downsampleSeriesEnvelope(series, maxPoints)
        : downsampleTrackPoints(series, maxPoints)
      : series;

  return sampled.map((sample, index) => ({
    index,
    elapsedSec: (sample.timecodeMs - elapsedOriginMs) / 1000,
    timecodeMs: sample.timecodeMs,
    value: transformSeriesSampleValue(shortName, sample.value, accXBaseline),
  }));
}

export function countTraceSamplesInWindow(
  session: MyChronSessionExport,
  startMs: number,
  endMs: number,
) {
  const counts = session.channels.map(
    (channel) => seriesInWindow(session.series[channel.shortName], startMs, endMs).length,
  );

  return Math.max(...counts, 1);
}

export function resolveLapTracePointBudget(
  session: MyChronSessionExport,
  startMs: number,
  endMs: number,
  lapDurationSec: number,
  viewDurationSec: number,
) {
  const available = countTraceSamplesInWindow(session, startMs, endMs);
  const zoomFactor = lapDurationSec / Math.max(viewDurationSec, LAP_TRACE_MIN_ZOOM_SPAN_SEC);
  const windowMs = Math.max(endMs - startMs, 1);
  const isZoomed = viewDurationSec < lapDurationSec * 0.98;

  if (isZoomed || zoomFactor >= 2) {
    return available;
  }

  const fromZoom = Math.ceil(640 * zoomFactor * 4);
  const fromSampleRate = Math.ceil(windowMs / 2);
  const fromDisplayWidth = Math.ceil(3200 * zoomFactor);

  return Math.min(available, Math.max(640, fromZoom, fromSampleRate, fromDisplayWidth));
}

export function buildChannelTrace(
  session: MyChronSessionExport,
  channel: TraceChannelSeriesKey,
  startMs: number,
  endMs: number,
  elapsedOriginMs: number,
  maxPoints?: number,
  accXBaseline: number | null = null,
): LapTracePoint[] {
  const shortName = TRACE_CHANNEL_SERIES[channel];
  const series = seriesInWindow(session.series[shortName], startMs, endMs);

  if (!series.length) {
    return [];
  }

  const sampled =
    maxPoints !== undefined && series.length > maxPoints
      ? channel === 'rpm'
        ? downsampleSeriesEnvelope(series, maxPoints)
        : downsampleTrackPoints(series, maxPoints)
      : series;

  return sampled.map((sample, index) => ({
    index,
    elapsedSec: (sample.timecodeMs - elapsedOriginMs) / 1000,
    timecodeMs: sample.timecodeMs,
    speed: channel === 'speed' ? sample.value : speedKphAtTime(session, sample.timecodeMs),
    rpm:
      channel === 'rpm' ? sample.value : nearestSeriesValue(session.series.RPM, sample.timecodeMs),
    steering:
      channel === 'steering'
        ? sample.value
        : nearestSeriesValue(session.series.StrA, sample.timecodeMs),
    accX:
      channel === 'accX'
        ? driverLongitudinalG(sample.value, accXBaseline)
        : driverLongitudinalG(
            nearestSeriesValue(session.series.AccX, sample.timecodeMs),
            accXBaseline,
          ),
    accY:
      channel === 'accY'
        ? driverLateralG(sample.value)
        : driverLateralG(nearestSeriesValue(session.series.AccY, sample.timecodeMs)),
  }));
}

export function buildLapTrace(
  session: MyChronSessionExport,
  startMs: number,
  endMs: number,
  maxPoints = 240,
  elapsedOriginMs?: number,
  accXBaseline: number | null = null,
): LapTracePoint[] {
  const wspdSeries = seriesInWindow(session.series.WSpd, startMs, endMs);
  const rpmSeries = seriesInWindow(session.series.RPM, startMs, endMs);
  const masterSeries = wspdSeries.length ? wspdSeries : rpmSeries;
  const elapsedBaseMs = elapsedOriginMs ?? startMs;

  if (!masterSeries.length) {
    return [];
  }

  const rpmBudget = rpmSeries.length
    ? Math.min(rpmSeries.length, Math.max(Math.floor(maxPoints * 0.9), maxPoints))
    : 0;
  const wspdBudget = wspdSeries.length
    ? Math.min(wspdSeries.length, Math.floor(maxPoints * 0.6))
    : Math.floor(maxPoints * 0.6);

  const wspdSampled = wspdSeries.length
    ? wspdSeries.length <= wspdBudget
      ? wspdSeries
      : downsampleTrackPoints(wspdSeries, wspdBudget)
    : downsampleTrackPoints(rpmSeries, wspdBudget);
  const rpmSampled = rpmSeries.length
    ? rpmSeries.length <= rpmBudget
      ? rpmSeries
      : downsampleSeriesEnvelope(rpmSeries, rpmBudget)
    : [];
  const mergeCap = Math.max(
    maxPoints,
    rpmSampled.length + wspdSampled.length,
    rpmSampled.length * 2,
  );
  const timecodes = capTraceTimecodes(mergeTraceTimecodes(wspdSampled, rpmSampled), mergeCap);

  return timecodes.map((timecodeMs, index) => ({
    index,
    elapsedSec: (timecodeMs - elapsedBaseMs) / 1000,
    timecodeMs,
    speed: speedKphAtTime(session, timecodeMs),
    rpm: nearestSeriesValue(session.series.RPM, timecodeMs),
    steering: nearestSeriesValue(session.series.StrA, timecodeMs),
    accX: driverLongitudinalG(nearestSeriesValue(session.series.AccX, timecodeMs), accXBaseline),
    accY: driverLateralG(nearestSeriesValue(session.series.AccY, timecodeMs)),
  }));
}

export function formatTraceClock(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds - minutes * 60;
  const paddedSeconds = remainder.toFixed(3).padStart(minutes > 0 ? 6 : 6, '0');
  return minutes > 0 ? `${minutes}:${paddedSeconds}` : paddedSeconds;
}

export function formatGap(seconds: number | null) {
  if (seconds === null) {
    return '-';
  }

  if (Math.abs(seconds) < 0.0005) {
    return '-';
  }

  return seconds > 0 ? `+${seconds.toFixed(3)}` : seconds.toFixed(3);
}

export function gForceAtTime(
  session: MyChronSessionExport,
  timecodeMs: number,
  accXBaseline: number | null = null,
): { lateralG: number | null; longitudinalG: number | null } {
  return {
    lateralG: driverLateralG(interpolatedSeriesValue(session.series.AccY, timecodeMs)),
    longitudinalG: driverLongitudinalG(
      interpolatedSeriesValue(session.series.AccX, timecodeMs),
      accXBaseline,
    ),
  };
}

function interpolateGpsSegment(segment: MyChronGpsPoint[], t: number): MyChronGpsPoint {
  if (segment.length === 0) {
    throw new Error('Cannot interpolate an empty GPS segment');
  }

  if (segment.length === 1) {
    return segment[0]!;
  }

  const clamped = Math.min(1, Math.max(0, t));
  const scaled = clamped * (segment.length - 1);
  const lo = Math.floor(scaled);
  const hi = Math.ceil(scaled);
  const weight = scaled - lo;
  const start = segment[lo]!;
  const end = segment[hi]!;

  return {
    lat: start.lat + (end.lat - start.lat) * weight,
    lon: start.lon + (end.lon - start.lon) * weight,
    speed: start.speed + (end.speed - start.speed) * weight,
    alt: start.alt + (end.alt - start.alt) * weight,
    timecodeMs: start.timecodeMs + (end.timecodeMs - start.timecodeMs) * weight,
    fix: start.fix,
    satellites: start.satellites,
  };
}

function lapSectorWindows(
  lap: MyChronLapRecord,
  boundaryFractions: [number, number],
): Array<[number, number]> {
  const { startMs, endMs } = lapWindow(lap);
  const duration = endMs - startMs;
  const boundary1 = startMs + duration * boundaryFractions[0];
  const boundary2 = startMs + duration * boundaryFractions[1];

  return [
    [startMs, boundary1],
    [boundary1, boundary2],
    [boundary2, endMs],
  ];
}

function bestSectorLapNumbers(sessionAnalysis: SessionAnalysis) {
  const rows = sessionAnalysis.lapRows.filter(
    (row): row is LapTableRow & { sectors: SectorTimesSec } => row.sectors !== null,
  );
  if (rows.length === 0) {
    return null;
  }

  let bestS1 = rows[0]!;
  let bestS2 = rows[0]!;
  let bestS3 = rows[0]!;

  for (const row of rows) {
    if (row.sectors.s1 < bestS1.sectors.s1) {
      bestS1 = row;
    }
    if (row.sectors.s2 < bestS2.sectors.s2) {
      bestS2 = row;
    }
    if (row.sectors.s3 < bestS3.sectors.s3) {
      bestS3 = row;
    }
  }

  return {
    s1: bestS1.lapNumber,
    s2: bestS2.lapNumber,
    s3: bestS3.lapNumber,
  };
}

export type ReplayScope = 'lap' | 'session' | 'segment';

export type LapSegmentRange = {
  lapNumber: number;
  startSec: number;
  endSec: number;
};

export type ReplayLapWindow = {
  lapNumber: number;
  startMs: number;
  endMs: number;
  durationSec: number;
};

export type ReplayTimeline = {
  scope: ReplayScope;
  durationSec: number;
  startTimecodeMs: number;
  endTimecodeMs: number;
  laps: ReplayLapWindow[];
};

export function buildLapReplayTimeline(
  session: MyChronSessionExport,
  lapNumber: number,
): ReplayTimeline | null {
  const lap = resolveRawLoggerLaps(session).find((entry) => entry.lapNumber === lapNumber);
  if (!lap) {
    return null;
  }

  const { startMs, endMs } = lapWindow(lap);

  return {
    scope: 'lap',
    durationSec: lap.durationMs / 1000,
    startTimecodeMs: startMs,
    endTimecodeMs: endMs,
    laps: [
      {
        lapNumber,
        startMs,
        endMs,
        durationSec: lap.durationMs / 1000,
      },
    ],
  };
}

export function buildSessionReplayTimeline(
  session: MyChronSessionExport,
  exclusions: LapExclusion[] = [],
): ReplayTimeline | null {
  const laps = racingLapsForAnalysis(session, exclusions);
  if (laps.length === 0) {
    return null;
  }

  const windows = laps.map((lap) => {
    const { startMs, endMs } = lapWindow(lap);
    return {
      lapNumber: lap.lapNumber,
      startMs,
      endMs,
      durationSec: lap.durationMs / 1000,
    };
  });

  const startTimecodeMs = windows[0]!.startMs;
  const endTimecodeMs = windows.at(-1)!.endMs;

  return {
    scope: 'session',
    durationSec: (endTimecodeMs - startTimecodeMs) / 1000,
    startTimecodeMs,
    endTimecodeMs,
    laps: windows,
  };
}

export function buildSegmentReplayTimeline(
  session: MyChronSessionExport,
  segment: LapSegmentRange,
): ReplayTimeline | null {
  const lap = racingLaps(session.laps).find((entry) => entry.lapNumber === segment.lapNumber);
  if (!lap) {
    return null;
  }

  const { startMs: lapStartMs } = lapWindow(lap);
  const startTimecodeMs = lapStartMs + segment.startSec * 1000;
  const endTimecodeMs = lapStartMs + segment.endSec * 1000;
  const durationSec = segment.endSec - segment.startSec;

  if (durationSec <= 0) {
    return null;
  }

  return {
    scope: 'segment',
    durationSec,
    startTimecodeMs,
    endTimecodeMs,
    laps: [
      {
        lapNumber: segment.lapNumber,
        startMs: startTimecodeMs,
        endMs: endTimecodeMs,
        durationSec,
      },
    ],
  };
}

export function lapWindowAtTimecode(timeline: ReplayTimeline, timecodeMs: number) {
  return timeline.laps.find((lap) => timecodeMs >= lap.startMs && timecodeMs <= lap.endMs) ?? null;
}

export function elapsedSecOnTimeline(timeline: ReplayTimeline, timecodeMs: number) {
  return Math.min(
    timeline.durationSec,
    Math.max(0, (timecodeMs - timeline.startTimecodeMs) / 1000),
  );
}

export function timecodeOnTimeline(timeline: ReplayTimeline, elapsedSec: number) {
  return timeline.startTimecodeMs + Math.min(Math.max(elapsedSec, 0), timeline.durationSec) * 1000;
}

export function lapElapsedSec(lap: ReplayLapWindow, timecodeMs: number) {
  return Math.min(lap.durationSec, Math.max(0, (timecodeMs - lap.startMs) / 1000));
}

export function traceValuesAtTime(
  session: MyChronSessionExport,
  timecodeMs: number,
  accXBaseline: number | null = null,
  seriesHints?: {
    rpm?: InterpolateMapPointHint;
    steering?: InterpolateMapPointHint;
    accX?: InterpolateMapPointHint;
    accY?: InterpolateMapPointHint;
  },
): Pick<LapTracePoint, 'speed' | 'rpm' | 'steering' | 'accX' | 'accY'> {
  const rpmHint = seriesHints?.rpm;
  const steeringHint = seriesHints?.steering;
  const accXHint = seriesHints?.accX;
  const accYHint = seriesHints?.accY;

  return {
    speed: speedKphAtTime(session, timecodeMs),
    rpm: interpolatedSeriesValueWithHint(session.series.RPM, timecodeMs, rpmHint),
    steering: interpolatedSeriesValueWithHint(session.series.StrA, timecodeMs, steeringHint),
    accX: driverLongitudinalG(
      interpolatedSeriesValueWithHint(session.series.AccX, timecodeMs, accXHint),
      accXBaseline,
    ),
    accY: driverLateralG(
      interpolatedSeriesValueWithHint(session.series.AccY, timecodeMs, accYHint),
    ),
  };
}

export function nearestMapPointIndex(points: TrackMapPoint[], timecodeMs: number) {
  if (points.length === 0) {
    return null;
  }

  let closestIndex = 0;
  let closestDelta = Math.abs(points[0]!.timecodeMs - timecodeMs);

  for (let index = 1; index < points.length; index += 1) {
    const delta = Math.abs(points[index]!.timecodeMs - timecodeMs);
    if (delta < closestDelta) {
      closestIndex = index;
      closestDelta = delta;
    }
  }

  return closestIndex;
}

export type InterpolateMapPointHint = {
  index: number;
};

function interpolateMapPointBetween(prev: TrackMapPoint, next: TrackMapPoint, timecodeMs: number) {
  const span = next.timecodeMs - prev.timecodeMs;
  const t = span === 0 ? 0 : (timecodeMs - prev.timecodeMs) / span;

  return {
    x: prev.x + (next.x - prev.x) * t,
    y: prev.y + (next.y - prev.y) * t,
    lat: prev.lat + (next.lat - prev.lat) * t,
    lon: prev.lon + (next.lon - prev.lon) * t,
    speedKph: prev.speedKph + (next.speedKph - prev.speedKph) * t,
  };
}

/** Replay-optimized interpolation — reuses a moving index while the playhead advances. */
export function interpolateMapPointAtTimeWithHint(
  points: TrackMapPoint[],
  timecodeMs: number,
  hint?: InterpolateMapPointHint,
): Pick<TrackMapPoint, 'x' | 'y' | 'lat' | 'lon' | 'speedKph'> | null {
  if (points.length === 0) {
    return null;
  }

  if (points.length === 1) {
    const point = points[0]!;
    if (hint) {
      hint.index = 0;
    }
    return { x: point.x, y: point.y, lat: point.lat, lon: point.lon, speedKph: point.speedKph };
  }

  const first = points[0]!;
  if (timecodeMs <= first.timecodeMs) {
    if (hint) {
      hint.index = 0;
    }
    return { x: first.x, y: first.y, lat: first.lat, lon: first.lon, speedKph: first.speedKph };
  }

  const last = points.at(-1)!;
  if (timecodeMs >= last.timecodeMs) {
    if (hint) {
      hint.index = Math.max(0, points.length - 2);
    }
    return { x: last.x, y: last.y, lat: last.lat, lon: last.lon, speedKph: last.speedKph };
  }

  let lo = hint ? Math.max(0, Math.min(hint.index, points.length - 2)) : 0;

  if (points[lo + 1]!.timecodeMs < timecodeMs) {
    while (lo < points.length - 2 && points[lo + 1]!.timecodeMs < timecodeMs) {
      lo += 1;
    }
  } else {
    while (lo > 0 && points[lo]!.timecodeMs > timecodeMs) {
      lo -= 1;
    }
  }

  while (lo < points.length - 2 && points[lo + 1]!.timecodeMs < timecodeMs) {
    lo += 1;
  }
  while (lo > 0 && points[lo]!.timecodeMs > timecodeMs) {
    lo -= 1;
  }

  if (hint) {
    hint.index = lo;
  }

  return interpolateMapPointBetween(points[lo]!, points[lo + 1]!, timecodeMs);
}

/** Pre-sampled lat/lon table for butter-smooth 60fps replay cursor motion. */
export type ReplayMotionCache = {
  startMs: number;
  stepMs: number;
  count: number;
  lat: Float64Array;
  lon: Float64Array;
};

const REPLAY_MOTION_STEP_MS = 8;
const MAX_REPLAY_MOTION_SAMPLES = 4000;

export function buildReplayMotionCache(
  points: TrackMapPoint[],
  stepMs = REPLAY_MOTION_STEP_MS,
): ReplayMotionCache | null {
  if (points.length < 2) {
    return null;
  }

  const startMs = points[0]!.timecodeMs;
  const endMs = points.at(-1)!.timecodeMs;
  if (endMs <= startMs) {
    return null;
  }

  const durationMs = endMs - startMs;
  const rawCount = Math.max(2, Math.floor(durationMs / stepMs) + 1);
  const count = Math.min(MAX_REPLAY_MOTION_SAMPLES, rawCount);
  const effectiveStepMs = durationMs / (count - 1);
  const startedAt = performance.now();
  const lat = new Float64Array(count);
  const lon = new Float64Array(count);
  const hint: InterpolateMapPointHint = { index: 0 };

  for (let index = 0; index < count; index += 1) {
    const timecodeMs = startMs + index * effectiveStepMs;
    const sample = interpolateMapPointAtTimeWithHint(points, timecodeMs, hint);
    if (!sample) {
      return null;
    }

    lat[index] = sample.lat;
    lon[index] = sample.lon;
  }

  sessionMapDebugDuration('buildReplayMotionCache', startedAt, {
    pointCount: points.length,
    sampleCount: count,
    lapDurationMs: Math.round(durationMs),
    stepMs: Math.round(effectiveStepMs),
  });

  return { startMs, stepMs: effectiveStepMs, count, lat, lon };
}

export function sampleReplayMotionCache(
  cache: ReplayMotionCache,
  timecodeMs: number,
): { lat: number; lon: number } {
  if (timecodeMs <= cache.startMs) {
    return { lat: cache.lat[0]!, lon: cache.lon[0]! };
  }

  const endMs = cache.startMs + (cache.count - 1) * cache.stepMs;
  if (timecodeMs >= endMs) {
    const last = cache.count - 1;
    return { lat: cache.lat[last]!, lon: cache.lon[last]! };
  }

  const fraction = (timecodeMs - cache.startMs) / cache.stepMs;
  const index = Math.floor(fraction);
  const blend = fraction - index;
  const nextIndex = Math.min(index + 1, cache.count - 1);

  return {
    lat: cache.lat[index]! + (cache.lat[nextIndex]! - cache.lat[index]!) * blend,
    lon: cache.lon[index]! + (cache.lon[nextIndex]! - cache.lon[index]!) * blend,
  };
}

/** Smooth replay cursor — linearly interpolate between bracketing GPS samples. */
export function interpolateMapPointAtTime(
  points: TrackMapPoint[],
  timecodeMs: number,
): Pick<TrackMapPoint, 'x' | 'y' | 'lat' | 'lon' | 'speedKph'> | null {
  if (points.length === 0) {
    return null;
  }

  if (points.length === 1) {
    const point = points[0]!;
    return { x: point.x, y: point.y, lat: point.lat, lon: point.lon, speedKph: point.speedKph };
  }

  const first = points[0]!;
  if (timecodeMs <= first.timecodeMs) {
    return { x: first.x, y: first.y, lat: first.lat, lon: first.lon, speedKph: first.speedKph };
  }

  const last = points.at(-1)!;
  if (timecodeMs >= last.timecodeMs) {
    return { x: last.x, y: last.y, lat: last.lat, lon: last.lon, speedKph: last.speedKph };
  }

  let lo = 1;
  let hi = points.length - 1;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (points[mid]!.timecodeMs < timecodeMs) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }

  const next = points[lo]!;
  const prev = points[lo - 1]!;
  return interpolateMapPointBetween(prev, next, timecodeMs);
}

export type MapReplayCursor = {
  id: string;
  points: TrackMapPoint[];
  timecodeMs: number | null;
  color: string;
  avatarUrl?: string | null;
  initials?: string | null;
  identityVariant?: number;
  label?: string;
  speedLabel?: string;
  stackIndex?: number;
  /** When true, the map camera follows this cursor during replay. */
  follow?: boolean;
};

export type TrackMapMarkerKind =
  | 'note'
  | 'sector'
  | 'sector-boundary'
  | 'section-boundary'
  | 'footage-start'
  | 'braking-soft'
  | 'braking-hard'
  | 'coasting';

export type TrackMapMarker = {
  id: string;
  kind: TrackMapMarkerKind;
  label: string;
  lat: number;
  lon: number;
  x: number;
  y: number;
  color: string;
  selected?: boolean;
};

export function getLapSectorWindows(
  lap: MyChronLapRecord,
  boundaryFractions: [number, number],
): Array<[number, number]> {
  return lapSectorWindows(lap, boundaryFractions);
}

export function filterMapPointsInWindow(
  points: TrackMapPoint[],
  startMs: number,
  endMs: number,
): TrackMapPoint[] {
  return points.filter((point) => point.timecodeMs >= startMs && point.timecodeMs <= endMs);
}

export function buildSectorMapMarkers(
  mapPoints: TrackMapPoint[],
  windows: Array<[number, number]>,
  selectedIndex: number | null,
): TrackMapMarker[] {
  if (mapPoints.length === 0 || windows.length === 0) {
    return [];
  }

  const markers: TrackMapMarker[] = [];

  for (const [index, [startMs, endMs]] of windows.entries()) {
    const midMs = startMs + (endMs - startMs) / 2;
    const mid = interpolateMapPointAtTime(mapPoints, midMs);
    if (!mid) {
      continue;
    }

    markers.push({
      id: `sector-${index}`,
      kind: 'sector',
      label: `S${index + 1}`,
      lat: mid.lat,
      lon: mid.lon,
      x: mid.x,
      y: mid.y,
      color: selectedIndex === index ? '#A855F7' : '#22D3EE',
      selected: selectedIndex === index,
    });
  }

  for (let index = 0; index < Math.min(2, windows.length); index += 1) {
    const boundaryMs = windows[index]![1];
    const point = interpolateMapPointAtTime(mapPoints, boundaryMs);
    if (!point) {
      continue;
    }

    markers.push({
      id: `sector-boundary-${index}`,
      kind: 'sector-boundary',
      label: '',
      lat: point.lat,
      lon: point.lon,
      x: point.x,
      y: point.y,
      color: 'rgba(255,255,255,0.85)',
    });
  }

  return markers;
}
